// Pure helpers for the dashboard charts: scales, paths, arcs, number and date formatting. No React, so they are easy to test.

// One colour per thing, the same everywhere on the dashboard (the validated dark-surface categorical palette, in its fixed order).
export const PALETTE = ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"];
export const ENTITY = { ai: PALETTE[0], inbox: PALETTE[1], chat: PALETTE[2], cowork: PALETTE[3], foundation: PALETTE[4], blogs: PALETTE[6] };
// Status colours: reserved for state, always shown with an icon and a word (never as a series).
export const STATUS = { good: "#0ca30c", warning: "#fab219", serious: "#ec835a", critical: "#d03b3b" };
// An ordered ramp for "more is lighter" on the dark surface (blue, steps 550 -> 400 -> 250 -> 150).
export const RAMP = ["#1c5cab", "#3987e5", "#86b6ef", "#b7d3f6"];
export const INK = { primary: "#f0efec", secondary: "#c3c2b7", muted: "#898781", grid: "#233A51", surface: "#13202D" };

export const sum = (values) => (values || []).reduce((a, b) => a + (Number(b) || 0), 0);

// 1,284 / 12.9K / 4.2M
export function compact(value) {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${(n / 1e6).toFixed(abs >= 1e7 ? 0 : 1).replace(/\.0$/, "")}M`;
  if (abs >= 1e4) return `${(n / 1e3).toFixed(abs >= 1e5 ? 0 : 1).replace(/\.0$/, "")}K`;
  return n.toLocaleString("en-US", { maximumFractionDigits: abs < 10 && !Number.isInteger(n) ? 1 : 0 });
}

export const money = (value) => `$${compact(Number(value) || 0)}`;

// A round top for an axis: the axis runs 0..max in `steps` equal, clean steps. niceScale(37) -> { max: 40, step: 10, ticks: [0,10,20,30,40] }
export function niceScale(rawMax, steps = 4) {
  const max = Math.max(1, Number(rawMax) || 1);
  const rough = max / steps;
  const power = 10 ** Math.floor(Math.log10(rough));
  const fraction = rough / power;
  const step = (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10) * power;
  const top = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= top + step / 1000; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
  return { max: top, step, ticks };
}

// "2026-11-04" -> "4 Nov"  (the date is read as a plain calendar day: no time zone shifts it)
export function formatDay(iso, withWeekday = false) {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso);
  const date = new Date(Date.UTC(y, m - 1, d));
  const base = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  return withWeekday ? `${date.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" })} ${base}` : base;
}

// Which of `count` x positions to label so that about `target` labels fit: always the first and the last.
export function tickIndexes(count, target = 6) {
  if (count <= 1) return [0];
  const stepSize = Math.max(1, Math.round((count - 1) / (target - 1)));
  const out = [];
  for (let i = 0; i < count; i += stepSize) out.push(i);
  if (out[out.length - 1] !== count - 1) {
    if (count - 1 - out[out.length - 1] < stepSize / 2) out[out.length - 1] = count - 1; else out.push(count - 1);
  }
  return out;
}

// x of the i-th of `count` points spread between left and right
export const xAt = (i, count, left, right) => (count <= 1 ? (left + right) / 2 : left + ((right - left) * i) / (count - 1));

// The index of the point nearest to a pointer at `x`
export function nearestIndex(x, count, left, right) {
  if (count <= 1) return 0;
  const t = (x - left) / (right - left);
  return Math.max(0, Math.min(count - 1, Math.round(t * (count - 1))));
}

// A straight "M x y L x y ..." path through the points
export function linePath(points) {
  return points.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join("");
}

// The same line closed down to the baseline: the soft wash under a line
export function areaPath(points, baseline) {
  if (!points.length) return "";
  const first = points[0];
  const last = points[points.length - 1];
  return `${linePath(points)}L${last[0].toFixed(1)},${baseline}L${first[0].toFixed(1)},${baseline}Z`;
}

// "+12%" / "-5%" / "0%" / "new" (when there is nothing to compare with)
export function deltaText(delta) {
  if (delta === null || delta === undefined) return "new";
  if (delta === 0) return "0%";
  return `${delta > 0 ? "+" : ""}${delta}%`;
}

export const percent = (part, total) => (total > 0 ? Math.round((100 * part) / total) : 0);

// ---- donut ----
const polar = (cx, cy, r, angle) => [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];

// The path of a ring segment between two angles (radians, 0 = 3 o'clock, clockwise)
export function arcPath(cx, cy, outer, inner, a0, a1) {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = polar(cx, cy, outer, a0);
  const [x1, y1] = polar(cx, cy, outer, a1);
  const [x2, y2] = polar(cx, cy, inner, a1);
  const [x3, y3] = polar(cx, cy, inner, a0);
  return `M${x0.toFixed(2)},${y0.toFixed(2)}A${outer},${outer} 0 ${large} 1 ${x1.toFixed(2)},${y1.toFixed(2)}L${x2.toFixed(2)},${y2.toFixed(2)}A${inner},${inner} 0 ${large} 0 ${x3.toFixed(2)},${y3.toFixed(2)}Z`;
}

// The ring segments of a donut: [{ ...slice, path, share }]. `gap` is the 2px surface gap between neighbours (as an angle at the middle of the ring).
export function donutArcs(slices, { cx = 60, cy = 60, outer = 56, inner = 38, gap = 2 } = {}) {
  const total = sum(slices.map((s) => s.count));
  if (!total) return [];
  const gapAngle = slices.filter((s) => s.count > 0).length > 1 ? gap / ((outer + inner) / 2) : 0;
  let angle = -Math.PI / 2;
  return slices.filter((s) => s.count > 0).map((s) => {
    const span = (s.count / total) * Math.PI * 2;
    const a0 = angle + gapAngle / 2;
    const a1 = Math.max(a0 + 0.001, angle + span - gapAngle / 2);
    angle += span;
    return { ...s, share: s.count / total, path: arcPath(cx, cy, outer, inner, a0, Math.min(a1, a0 + Math.PI * 2 - 0.001)) };
  });
}

// Slices with the colour of each fixed by its position in the list (never cycled): more than `max` fold into one "Other".
export function foldSlices(rows, colors = PALETTE, max = 6) {
  const list = (rows || []).filter((r) => r.count > 0);
  let kept = list;
  if (list.length > max) kept = [...list.slice(0, max - 1), { key: "_other", label: "Other", count: sum(list.slice(max - 1).map((r) => r.count)) }];
  return kept.map((r, i) => ({ ...r, color: r.color || (r.key === "_other" ? INK.muted : colors[i % colors.length]) }));
}

// A greeting for the time of day
export const greeting = (hour) => (hour < 5 ? "Working late" : hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening");

// "3 minutes ago" / "2 days ago" / "12 Oct"
export function timeAgo(iso, now = Date.now()) {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)} d ago`;
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
